import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ThumbsUpDownOutlinedIcon from '@mui/icons-material/ThumbsUpDownOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Button, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import {
  fetchGoogleCalendarEvents,
  fetchOutlookCalendarEvents,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  ICalendarAccount,
  IEvent,
  IOutlookCalendarAccount,
  IOutlookCalendarEvent,
} from 'wisestep-api';
import {
  getDateUsingFormat,
  GoogleMeetIcon,
  MicrosoftTeamsIcon,
  WsDialog,
  WsDrawer,
  WsIconText,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import StageStatusCalendarEvent from '../../../components/CalendarEvent/StageStatusCalendarEvent';
import { useFetch } from '../../../hooks/useFetch';
import { useFetchDeps } from '../../../hooks/useFetchDeps';
import useCopyToClipboard from '../../candidates/talentpool/hooks/useCopyToClipboard';
import { CalendarEventCardEditDialog } from '../../home/components/CalendarBoard';

export const EventsTab = ({ setTabIndex, setEvalTemplateId, candidate, setFlag, state }) => {
  const dayStart = new Date('2023-11-22T18:30:00.777Z');
  dayStart.setHours(0, 0, 0);
  const dayEnd = new Date();
  dayEnd.setHours(23, 59, 59);
  dayEnd.setFullYear(dayEnd.getFullYear() + 1);
  const [eventsFlag, setEventsFlag] = useState(false);
  const [calendarEventAccountsGoogle] = useFetch<ICalendarAccount>(getCalendarAccounts);
  const [calendarAccountsOutlook] = useFetch<IOutlookCalendarAccount>(getOutlookCalendarAccounts);
  const [googleEvents] = useFetchDeps<IEvent[]>(
    ([calendarEventAccountsGoogle]) =>
      fetchGoogleCalendarEvents({
        googleOAuth: calendarEventAccountsGoogle?.[0],
        from: dayStart,
        attendees: [],
        entityId: null,
        entityType: '',
        eventDescription: '',
        eventSummary: '',
        location: '',
        reminders: [],
        to: dayEnd,
      }),
    [calendarEventAccountsGoogle, eventsFlag]
  );
  const [outlookEvents] = useFetchDeps<IEvent[]>(
    ([calendarEventAccountsOutlook]) =>
      fetchOutlookCalendarEvents({
        outlookOAuthToken: calendarEventAccountsOutlook?.[0],
        from: dayStart,
        attendees: [],
        entityId: null,
        entityType: '',
        eventDescription: '',
        eventSummary: '',
        location: '',
        reminders: [],
        to: dayEnd,
      }),
    [calendarAccountsOutlook, eventsFlag]
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarObj, setCalendarObj] = useState<IEvent>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [, copy] = useCopyToClipboard();

  return (
    <>
      <Stack p={2}>
        <Button
          onClick={() => setDrawerOpen(true)}
          sx={{ alignSelf: 'flex-end' }}
          variant="contained"
        >
          Add Event
        </Button>
        {_(
          [
            ...(googleEvents?.map((e) => ({ ...e, type: 'GOOGLE' })) ?? []),
            ...(outlookEvents?.map((e) => ({ ...e, type: 'OUTLOOK' })) ?? []),
          ]
            .filter((e) => e.jobCandidateInteractionId === candidate.id)
            .sort((a, b) => +b.from - +a.from)
        )
          .groupBy((value) => new Date(value.from).toDateString())
          .map((events, date) => (
            <Stack key={date} sx={{ mt: 3 }}>
              <WsTypography opacity>{date}</WsTypography>
              {events.map((e: IEvent, i) => (
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mt: 3 }} key={i}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Stack>
                      <WsTypography
                        cursor="pointer"
                        variant={WsTypoVariants.SUB_TITLE}
                        onClick={() => {
                          setCalendarObj(e);
                          setDialogOpen(true);
                        }}
                      >
                        {e.eventSummary}
                      </WsTypography>
                      <WsTypography opacity>{e.eventDescription}</WsTypography>
                      <Stack direction="row" px="11px" sx={{ opacity: '65%', mt: 1 }} spacing={2}>
                        <WsIconText
                          icon={<CalendarMonthOutlinedIcon sx={{ fontSize: '20px' }} />}
                          text={`${getDateUsingFormat(
                            new Date(e.from).toISOString(),
                            'hh:mm a'
                          )} - ${getDateUsingFormat(
                            new Date(e.to).toISOString(),
                            'hh:mm a'
                          )}, ${getDateUsingFormat(new Date(e.from).toISOString(), 'D MMMM YYYY')}`}
                          textSize={WsTypoVariants.BODY1}
                        />
                        <WsIconText
                          icon={<WorkOutlineIcon sx={{ fontSize: '20px' }} />}
                          text={candidate.mappedJobTitle}
                          textSize={WsTypoVariants.BODY1}
                        />
                        <Button
                          startIcon={<ThumbsUpDownOutlinedIcon sx={{ fontSize: '20px' }} />}
                          sx={{ color: 'text.primary' }}
                          onClick={() => {
                            setEvalTemplateId(e.evalTemplateId);
                            setTabIndex(4);
                            setFlag((flag) => !flag);
                          }}
                        >
                          Evaluation
                        </Button>
                      </Stack>
                    </Stack>
                    {e.meeting && (
                      <Tooltip title="Meeting Url (Click to Copy)">
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            copy(e?.meeting ?? '');
                          }}
                        >
                          {e['type'] === 'GOOGLE' ? <GoogleMeetIcon /> : <MicrosoftTeamsIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ))
          .value()}
      </Stack>
      <WsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fullHeight>
        <StageStatusCalendarEvent
          onClose={() => setDrawerOpen(false)}
          onSave={() => setDrawerOpen(false)}
          fullWidth
          candidate={state?.candidate}
        />
      </WsDrawer>
      <CalendarEventCardEditDialog
        open={dialogOpen}
        calendarObj={calendarObj as unknown as IOutlookCalendarEvent}
        onEdit={() => setEditDialogOpen(true)}
        onClose={() => setDialogOpen(false)}
        onCopy={() => {}}
        onDelete={() => {}}
      />
      <WsDialog openDialog={editDialogOpen}>
        <StageStatusCalendarEvent
          onClose={() => setEditDialogOpen(false)}
          onSave={() => {
            setDrawerOpen(false);
            setDialogOpen(false);
            setEventsFlag((flag) => !flag);
          }}
          fullWidth
          candidate={state?.candidate}
          defaultValues={{
            ...calendarObj,
            attendees: calendarObj?.attendees?.map((attendee) => ({ email: attendee })),
          }}
          calendarType={calendarObj?.['type']}
        />
      </WsDialog>
    </>
  );
};
